import React, { useState, useEffect } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";

// Icons
const ArrowLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
  </svg>
);

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
  </svg>
);

const UserPlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15,14C12.33,14 7,15.33 7,18V20H23V18C23,15.33 17.67,14 15,14M6,10V7H4V10H1V12H4V15H6V12H9V10M15,12A4,4 0 0,0 19,8A4,4 0 0,0 15,4A4,4 0 0,0 11,8A4,4 0 0,0 15,12Z"/>
  </svg>
);

const PulseSurveyHeader = ({ 
  title,
  description,
  createdBy,
  createdDate,
  updatedDate,
  surveyIsAnonymous,
  surveyIsOpen,
  task_template_id,
  compliance,
  onSelectClick,
  onChatClick,
  onCloseSurvey,
  onArchiveSurvey,
  backAction,
  showSelectButton = true
}) => {
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const formatDateTime = (date, time) => {
    if (!date) return "N/A";
    return `${new Date(date).toLocaleDateString()}${time ? ` at ${time}` : ''}`;
  };

  const getCompletionRate = () => {
    if (compliance?.percentage !== undefined) {
      return compliance.percentage;
    }
    if (compliance?.respondedCount && compliance?.totalParticipants) {
      return Math.round((compliance.respondedCount / compliance.totalParticipants) * 100);
    }
    return 0;
  };

  return (
    <Container>
      {/* Header */}
      <HeaderSection>
        {backAction && (
          <BackButton onClick={backAction}>
            <ArrowLeftIcon />
            <span>Back to Surveys</span>
          </BackButton>
        )}

        <HeaderContent>
          <IconWrapper>
            <CalendarIcon />
          </IconWrapper>
          <HeaderText>
            <Title>{title}</Title>
            <Subtitle>
              Pulse Survey • {getCompletionRate()}% completion rate
            </Subtitle>
          </HeaderText>
        </HeaderContent>

        <HeaderActions>
          {showSelectButton && onSelectClick && (
            <ActionButton onClick={onSelectClick}>
              <span>☰</span>
              <span>Select</span>
            </ActionButton>
          )}

          {onChatClick && (
            <ActionButton onClick={onChatClick}>
              <UserPlusIcon />
              <span>Chat with Data</span>
            </ActionButton>
          )}
        </HeaderActions>
      </HeaderSection>

      {/* Survey Details */}
      <SurveyDetailsSection>
        <SurveyDetailsCard>
          <CardHeader>
            <CardTitle>Survey Details</CardTitle>
          </CardHeader>
          <CardContent>
            <DetailsGrid>
              <DetailsItem>
                <DetailsLabel>Description</DetailsLabel>
                <DetailsValue>
                  {description || "No description provided"}
                </DetailsValue>
              </DetailsItem>
              <DetailsItem>
                <DetailsLabel>Created By</DetailsLabel>
                <DetailsValue>{createdBy || "Unknown"}</DetailsValue>
              </DetailsItem>
              <DetailsItem>
                <DetailsLabel>Created Date</DetailsLabel>
                <DetailsValue>{formatDate(createdDate)}</DetailsValue>
              </DetailsItem>
              <DetailsItem>
                <DetailsLabel>Updated Date</DetailsLabel>
                <DetailsValue>{formatDate(updatedDate)}</DetailsValue>
              </DetailsItem>
              <DetailsItem>
                <DetailsLabel>Anonymous Survey</DetailsLabel>
                <DetailsValue>{surveyIsAnonymous ? "Yes" : "No"}</DetailsValue>
              </DetailsItem>
              <DetailsItem>
                <DetailsLabel>Survey Status</DetailsLabel>
                <DetailsValue>
                  {surveyIsOpen ? "Active" : "Closed"}
                </DetailsValue>
              </DetailsItem>
            </DetailsGrid>
          </CardContent>
        </SurveyDetailsCard>

        <StatsCards>
          <StatCard>
            <StatValue>{compliance?.totalParticipants || 0}</StatValue>
            <StatLabel>Total Recipients</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{compliance?.respondedCount || 0}</StatValue>
            <StatLabel>Completed</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>
              {(compliance?.totalParticipants || 0) -
                (compliance?.respondedCount || 0)}
            </StatValue>
            <StatLabel>Pending</StatLabel>
          </StatCard>
        </StatsCards>
      </SurveyDetailsSection>
    </Container>
  );
};

PulseSurveyHeader.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  createdBy: PropTypes.string,
  createdDate: PropTypes.string,
  updatedDate: PropTypes.string,
  surveyIsAnonymous: PropTypes.bool,
  surveyIsOpen: PropTypes.bool,
  task_template_id: PropTypes.number,
  compliance: PropTypes.shape({
    percentage: PropTypes.number,
    respondedCount: PropTypes.number,
    totalParticipants: PropTypes.number,
  }),
  onSelectClick: PropTypes.func,
  onChatClick: PropTypes.func,
  onCloseSurvey: PropTypes.func,
  onArchiveSurvey: PropTypes.func,
  backAction: PropTypes.func,
  showSelectButton: PropTypes.bool,
};

export default PulseSurveyHeader;

// Styled Components
const Container = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
  animation: fadeIn 0.6s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(16px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
    border-color: #c0c0c0;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 1;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 7px;
`;

const HeaderText = styled.div``;

const Title = styled.h1`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 24px;
  color: #414141;
  margin: 0 0 4px 0;
`;

const Subtitle = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 14px;
  color: #7e7e7e;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: #476dfa;
  border: none;
  border-radius: 7px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #3d5ce6;
  }
`;

const SurveyDetailsSection = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 20px;
  margin-bottom: 24px;
  align-items: stretch;        /* make both columns match row height */
  min-height: 280px;           /* ensures a reasonable base height */
`;

const SurveyDetailsCard = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  height: 100%;                /* stretch to full row height */
`;

const CardHeader = styled.div`
  padding: 16px 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
`;

const CardTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #414141;
  margin: 0;
`;

const CardContent = styled.div`
  padding: 20px;
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const DetailsItem = styled.div``;

const DetailsLabel = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  color: #7e7e7e;
  margin-bottom: 4px;
`;

const DetailsValue = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #414141;
`;

const StatsCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;                /* match SurveyDetailsCard height */
  min-height: 100%;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  padding: 16px;
  text-align: center;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  /* Three cards, two gaps (2 * 12px = 24px) */
  flex: 0 0 calc((100% - 24px) / 3);
  height: calc((100% - 24px) / 3);
`;

const StatValue = styled.div`
  font-family: "Poppins";
  font-weight: 700;
  font-size: 28px;
  color: #476dfa;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  color: #7e7e7e;
`;